import { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { CreateCustomShopData } from "../types/User";
import { Shop } from "../types/Shop";
import ShopModal from "./ShopModal";

interface AddCustomShopProps {
  userLocation: [number, number] | null;
  onShopCreated?: (shop: any) => void;
  isDarkMode?: boolean;
  hideButton?: boolean;
  showFilters?: boolean;
}

export default function AddCustomShop({ userLocation, onShopCreated, isDarkMode = false, hideButton = false, showFilters = false }: AddCustomShopProps) {
  const [showButton, setShowButton] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();

  // Show the + button only when user is logged in and has location
  useEffect(() => {
    setShowButton(!!user && !!userLocation);
  }, [user, userLocation]);

  const handleCreateShop = async (shopData: CreateCustomShopData | Shop) => {
    // Cast to CreateCustomShopData since we're only creating new shops
    const createData = shopData as CreateCustomShopData;
    if (!user) {
      alert("You must be logged in to create a custom shop");
      return;
    }

    setIsSubmitting(true);
    try {
      const token = await user.getIdToken();
      const response = await fetch("/api/shops", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(createData),
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(`Failed to create shop: ${response.status} - ${responseText}`);
      }

      const newShop = JSON.parse(responseText);

      onShopCreated?.(newShop);
      setShowModal(false);
    } catch (error) {
      console.error("Error creating shop:", error);
      alert(`Failed to create shop: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showButton) return null;

  return (
    <>
      {/* Floating + Button */}
      {!showModal && !hideButton && (
        <button
          onClick={() => setShowModal(true)}
          className={`fixed bottom-24 right-4 md:bottom-6 md:right-6 w-14 h-14 rounded-full shadow-lg z-40 transition-all hover:scale-105 ${isDarkMode ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-blue-600 hover:bg-blue-700 text-white"
            }`}
          title="Add Custom Shop"
        >
          <Plus className="w-6 h-6 mx-auto" />
        </button>
      )}

      {/* Shop Modal */}
      <ShopModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleCreateShop}
        userLocation={userLocation}
        isDarkMode={isDarkMode}
        isSubmitting={isSubmitting}
        showFilters={showFilters}
      />
    </>
  );
}
